import { Box } from '@chakra-ui/react'
import { flow } from '@helo/utils'
import { Group } from '@visx/group'
import { scaleBand, scaleLinear, scalePoint } from '@visx/scale'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import { RenderPipeline } from '../../RenderPipeline'
import type { ChartAxisOutput } from '../ChartAxis'
import type { ChartCartesianOutput } from '../ChartCartesian'
import { ChartWrapperConstants } from './ChartWrapper.constants'
import {
  type ChartWrapperOptions,
  chartWrapperOptions,
} from './ChartWrapper.options'
import {
  ChartWrapperInputParts,
  ChartWrapperOutputParts,
  ChartWrapperUtils,
} from './ChartWrapper.parts'

export { chartWrapperOptions } from './ChartWrapper.options'
export type { ChartWrapperOptions } from './ChartWrapper.options'

/** Pipeline input consumed by ChartWrapper.setup. */
export type ChartWrapperInput = ChartWrapperOptions

export type ChartWrapperPadding = {
  top: number
  right: number
  bottom: number
  left: number
}

export type ChartWrapperSlice = {
  chartHeight: number
  /** Static baseline; Wrapper recomputes from axis/data chrome at render time. */
  padding: ChartWrapperPadding
}

/** Pipeline output slice — namespaced wrapper config. */
export type ChartWrapperOutput = {
  wrapper: ChartWrapperSlice
}

type ChartWrapperScaleContext =
  | {
      orientation: 'vertical'
      xScale:
        | ReturnType<typeof scalePoint<string>>
        | ReturnType<typeof scaleBand<string>>
      yScale: ReturnType<typeof scaleLinear<number>>
    }
  | {
      orientation: 'horizontal'
      xScale: ReturnType<typeof scaleLinear<number>>
      yScale:
        | ReturnType<typeof scalePoint<string>>
        | ReturnType<typeof scaleBand<string>>
    }

/** Plot layout contract exposed to render features inside the wrapper. */
export type ChartPlotLayout = {
  width: number
  height: number
  innerWidth: number
  innerHeight: number
  padding: ChartWrapperPadding
  scales?: ChartWrapperScaleContext
}

export type ChartWrapperContextValue = ChartPlotLayout

const ChartWrapperContext = createContext<ChartWrapperContextValue | null>(null)

export const useChartWrapperContext = () => {
  const context = useContext(ChartWrapperContext)
  if (context == null) {
    throw new Error(
      'useChartWrapperContext must be used within ChartWrapper.Wrapper',
    )
  }
  return context
}

const chartWrapperSetup = () =>
  RenderPipeline.createFeatureSetup<ChartWrapperInput, ChartWrapperOutput>(
    flow(
      ChartWrapperInputParts.setupInput(),
      ChartWrapperOutputParts.setWrapper(),
    ),
  )

export interface ChartWrapperProps {
  renderConfig: ChartWrapperOutput &
    Partial<ChartCartesianOutput> &
    Partial<ChartAxisOutput>
  children?: ReactNode
}

const Wrapper = ({ renderConfig, children }: ChartWrapperProps) => {
  const { wrapper, cartesian, axis } = renderConfig
  const { chartHeight } = wrapper
  const plotOrientation = cartesian?.plotOrientation ?? 'vertical'
  const categoryScale = cartesian?.categoryScale ?? 'point'
  const bandPack = cartesian?.bandPack
  const xDomain = cartesian?.xDomain
  const yDomain = cartesian?.yDomain
  const yTicks = cartesian?.yTicks
  const showXAxisTicks = axis?.x.showTicks
  const showYAxisTicks = axis?.y.showTicks
  const xAxisLabel = axis?.x.label
  const yAxisLabel = axis?.y.label
  const xTickLineCount = axis?.xTickLineCount
  const xTickLabelOrientation = axis?.xTickLabelOrientation
  const yTickLabelOrientation = axis?.yTickLabelOrientation
  const xTickLabels = axis?.xTickLabels
  const yTickLabels = axis?.yTickLabels

  const padding = useMemo(
    () =>
      ChartWrapperUtils.resolvePadding({
        showXAxisTicks,
        showYAxisTicks,
        xDomain,
        yTicks,
        xAxisLabel,
        yAxisLabel,
        xTickLineCount,
        xTickLabelOrientation,
        yTickLabelOrientation,
        xTickLabels,
        yTickLabels,
        plotOrientation,
      }),
    [
      showXAxisTicks,
      showYAxisTicks,
      xDomain,
      yTicks,
      xAxisLabel,
      yAxisLabel,
      xTickLineCount,
      xTickLabelOrientation,
      yTickLabelOrientation,
      xTickLabels,
      yTickLabels,
      plotOrientation,
    ],
  )

  const totalHeight = ChartWrapperUtils.resolveTotalHeight(chartHeight, padding)
  /**
   * Do **not** use `@visx/responsive` `useParentSize` here.
   *
   * It pushes ResizeObserver results through `requestAnimationFrame`, so when
   * an ancestor locks width (FullScreenOverlay close FLIP) the SVG keeps the
   * old width for a frame and flickers. Portal reparent can also report 0;
   * treat 0 as “keep last width”, never as `fallbackWidthPx`.
   *
   * See FullScreenOverlay `requirements.mdc` FSO-R-7 gotcha.
   */
  const [parentNode, setParentNode] = useState<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(ChartWrapperConstants.fallbackWidthPx)
  const setParentRef = useCallback((node: HTMLDivElement | null) => {
    setParentNode(node)
  }, [])

  const syncWidthFromNode = useCallback((node: HTMLDivElement) => {
    const next = node.offsetWidth
    if (next <= 0) return
    setWidth(prev => (Math.abs(prev - next) < 0.5 ? prev : next))
  }, [])

  // After every commit (e.g. ancestor shellSizeLock), read layout before paint.
  // setState here is intentional: reconcile SVG width to offsetWidth in the
  // same frame as the shell lock, before the browser paints.
  useLayoutEffect(() => {
    if (parentNode == null) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- layout sync
    syncWidthFromNode(parentNode)
  })

  // Window / flex resizes that do not re-render this tree.
  useLayoutEffect(() => {
    if (parentNode == null) return
    const observer = new ResizeObserver(() => {
      syncWidthFromNode(parentNode)
    })
    observer.observe(parentNode)
    return () => observer.disconnect()
  }, [parentNode, syncWidthFromNode])

  const height = totalHeight

  const innerWidth = Math.max(0, width - padding.left - padding.right)
  const innerHeight = Math.max(0, height - padding.top - padding.bottom)
  const hasPlotSize =
    Number.isFinite(innerWidth) &&
    Number.isFinite(innerHeight) &&
    innerWidth > 0 &&
    innerHeight > 0

  const contextValue: ChartWrapperContextValue =
    xDomain == null || yDomain == null || !hasPlotSize
      ? {
          width,
          height,
          innerWidth: hasPlotSize ? innerWidth : 0,
          innerHeight: hasPlotSize ? innerHeight : 0,
          padding,
        }
      : {
          width,
          height,
          innerWidth,
          innerHeight,
          padding,
          scales: ChartWrapperUtils.buildScales({
            xDomain,
            yDomain,
            innerWidth,
            innerHeight,
            plotOrientation,
            categoryScale,
            bandPack,
          }),
        }

  return (
    <Box
      ref={setParentRef}
      flex="1"
      minW={0}
      minH={0}
      h={`${totalHeight}px`}
      alignSelf="stretch"
    >
      <ChartWrapperContext.Provider value={contextValue}>
        <svg width={width} height={height} overflow="visible">
          <Group left={padding.left} top={padding.top}>
            {children}
          </Group>
        </svg>
      </ChartWrapperContext.Provider>
    </Box>
  )
}

export const ChartWrapper = {
  options: chartWrapperOptions,
  Wrapper,
  setup: chartWrapperSetup,
  padding: ChartWrapperConstants.baselinePadding,
}
